import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd"
import { Delete, Edit, MoreVert, TextSnippetOutlined } from "@mui/icons-material"
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material"
import { ReportNodeImageBox } from "components/ReportNodeImage/ReportNodeImageBox"
import { ReportNodeTableBox } from "components/ReportNodeTable/ReportNodeTableBox"
import { ReportNodeTextBox } from "components/ReportNodeText/ReportNodeTextBox"
import { Chapter, DataNode, NodeDataType } from "graphql/types"
import { useStoreState } from "hooks/storeHooks"
import { useDataNodeChanged } from "hooks/useDataNodeChanged"
import { useEffect, useState } from "react"
import { isNodeImage, isNodeTable, isNodeText } from "store/typeGuards"

type ReportDataContentProps = {
  chapter: Chapter
  onEdit: (type: NodeDataType, node: DataNode) => void
  onDelete: (id: string) => void
  onSave: (data: DataNode[]) => void
}

export const ReportDataContent = (props: ReportDataContentProps) => {
  const { chapter, onEdit, onDelete, onSave } = props
  const [items, setItems] = useState<DataNode[]>(chapter.data)
  const [menuAnchor, setMenuAnchor] = useState<{ [key: string]: HTMLElement | null }>({})
  const { textBlocks } = useStoreState((state) => state)
  const { hasDataNodeChanged } = useDataNodeChanged()

  useEffect(() => {
    setItems(chapter.data)
  }, [chapter.data])

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, itemId: string) => {
    setMenuAnchor((prev) => ({ ...prev, [itemId]: event.currentTarget }))
  }

  const handleMenuClose = (itemId: string) => {
    setMenuAnchor((prev) => ({ ...prev, [itemId]: null }))
  }

  // Helper function to get textblock name by ID
  const getTextBlockName = (textBlockId: string): string => {
    const textBlock = textBlocks.find((tb) => tb.id === textBlockId)
    return textBlock?.name || "Unbekannter Textblock"
  }

  const handleOnDragEnd = (result: DropResult<string>) => {
    if (!result.destination) return
    const reorderedItems = Array.from(items)
    const [reorderedItem] = reorderedItems.splice(result.source.index, 1)
    reorderedItems.splice(result.destination.index, 0, reorderedItem)
    setItems(reorderedItems)

    onSave(reorderedItems)
  }

  return (
    <>
      {items.length === 0 && chapter.subChapter?.length === 0 && (
        <Typography sx={{ pl: 3 }}>n/a</Typography>
      )}
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable droppableId="components">
          {(provided) => (
            <Box {...provided.droppableProps} ref={provided.innerRef}>
              {items.map((item, index) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(provided) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      sx={{
                        display: "flex",
                        alignItems: "end",
                        justifyContent: "space-between",
                        width: "100%",
                        pl: 3,
                        mb: 2,
                      }}
                    >
                      {isNodeText(item) && <ReportNodeTextBox data={item} />}
                      {isNodeTable(item) && <ReportNodeTableBox data={item} />}
                      {isNodeImage(item) && <ReportNodeImageBox data={item} />}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-end",
                          alignItems: "center",
                        }}
                      >
                        {item.originalTextblockId && item.originalTextblockId !== "" && (
                          <Tooltip
                            title={
                              hasDataNodeChanged(item)
                                ? `Block aus Textblock "${getTextBlockName(item.originalTextblockId)}" (geändert)`
                                : `Block aus Textblock "${getTextBlockName(item.originalTextblockId)}"`
                            }
                          >
                            <TextSnippetOutlined
                              fontSize="small"
                              sx={{
                                color: hasDataNodeChanged(item) ? "warning.main" : "inherit",
                              }}
                            />
                          </Tooltip>
                        )}
                        {(item.editable || item.deletable) && (
                          <>
                            <Tooltip title="Aktionen">
                              <IconButton
                                onClick={(event) => handleMenuOpen(event, item.id)}
                                size="small"
                              >
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Menu
                              anchorEl={menuAnchor[item.id]}
                              open={Boolean(menuAnchor[item.id])}
                              onClose={() => handleMenuClose(item.id)}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                              }}
                              transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                              }}
                            >
                              {item.editable && (
                                <MenuItem
                                  onClick={() => {
                                    onEdit(item.dataType, item)
                                    handleMenuClose(item.id)
                                  }}
                                >
                                  <Edit fontSize="small" sx={{ mr: 1 }} />
                                  Bearbeiten
                                </MenuItem>
                              )}
                              {item.deletable && (
                                <MenuItem
                                  onClick={() => {
                                    onDelete(item.id)
                                    handleMenuClose(item.id)
                                  }}
                                >
                                  <Delete fontSize="small" sx={{ mr: 1 }} />
                                  Löschen
                                </MenuItem>
                              )}
                            </Menu>
                          </>
                        )}
                      </Box>
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
    </>
  )
}
